import type { ReactNode, FC } from "react";
import {
  Card,
  Flex,
  Grid,
  Ruler,
  Text,
  ValueParser,
  type ValueType,
} from "@huub-web/ui";
import { useTranslation } from "react-i18next";

import {
  cardInnerStyle,
  cardContentStyle,
  itemContentStyle,
  firstCardStyle,
  singleItemStyle,
  containerStyle,
} from "./SituationComparison.css";

export type SituationComparisonProps = {
  yourSituationCTA: ReactNode;
  neededSituationCTA: ReactNode;
  yourSituationValue: string | number;
  yourSituationMonthlyValue: string | number;
  neededSituationValue: string | number;
  neededSituationMonthlyValue: string | number;
  yourSituationDescription: string;
  /**
   * Override for the right card title (e.g. "Benodigde hypotheek voor oversluiten").
   * Falls back to the default "neededMortgage" translation when omitted.
   */
  neededSituationTitle?: string;
  neededSituationDescription: string;
  hasPartner?: boolean;
  /**
   * When true, only the left card is rendered. The left card's description is
   * replaced with `neededSituationDescription` since it is the more relevant
   * copy for this context (e.g. the renovate/raise-mortgage purpose).
   */
  hideNeededSituation?: boolean;
};

export const SituationComparison: FC<SituationComparisonProps> = ({
  yourSituationCTA,
  neededSituationCTA,
  yourSituationValue,
  yourSituationMonthlyValue,
  neededSituationValue,
  neededSituationMonthlyValue,
  yourSituationDescription,
  neededSituationTitle,
  neededSituationDescription,
  hasPartner = false,
  hideNeededSituation = false,
}) => {
  const { t } = useTranslation();

  return (
    <Card
      border="default"
      padding="s"
      className={containerStyle({ hideNeededSituation })}
    >
      {hideNeededSituation ? (
        <Card className={firstCardStyle}>
          <Flex gap="s" direction="column" className={singleItemStyle}>
            <SituationComparisonItem
              title={
                neededSituationTitle ??
                t(
                  `maxMortgageWizard.result.situationComparison.yourMaxMortgage.${hasPartner ? "plural" : "singular"}`,
                )
              }
              monthlyTitle={t(
                "maxMortgageWizard.result.situationComparison.monthlyTitle",
              )}
              value={{ value: neededSituationValue, type: "money" }}
              monthlyValue={{
                value: neededSituationMonthlyValue,
                type: "money",
              }}
            />
            <Text variant="bodyS" color="currentColor50" markdown grow>
              {neededSituationDescription}
            </Text>
            {neededSituationCTA}
          </Flex>
        </Card>
      ) : (
        <Grid variant="twoColumn">
          <Card className={firstCardStyle}>
            <SituationComparisonItem
              title={t(
                `maxMortgageWizard.result.situationComparison.yourMaxMortgage.${hasPartner ? "plural" : "singular"}`,
              )}
              monthlyTitle={t(
                "maxMortgageWizard.result.situationComparison.monthlyTitle",
              )}
              value={{ value: yourSituationValue, type: "money" }}
              monthlyValue={{ value: yourSituationMonthlyValue, type: "money" }}
            />
            <Text variant="bodyS" color="currentColor50" markdown grow>
              {yourSituationDescription}
            </Text>
            {neededSituationCTA}
          </Card>
          <Card
            border="gradient"
            innerClassName={cardInnerStyle}
            contentClassName={cardContentStyle}
            elevated
          >
            <SituationComparisonItem
              title={
                neededSituationTitle ??
                t("maxMortgageWizard.result.situationComparison.neededMortgage")
              }
              monthlyTitle={t(
                "maxMortgageWizard.result.situationComparison.monthlyTitle",
              )}
              value={{ value: neededSituationValue, type: "money" }}
              monthlyValue={{
                value: neededSituationMonthlyValue,
                type: "money",
              }}
              primary
            />
            <Text variant="bodyS" color="currentColor50" markdown grow>
              {neededSituationDescription}
            </Text>
            {yourSituationCTA}
          </Card>
        </Grid>
      )}
    </Card>
  );
};

type SituationComparisonItemProps = {
  title: string;
  monthlyTitle: string;
  value: ValueType<string | number>;
  monthlyValue: ValueType<string | number>;
  primary?: boolean;
};

const SituationComparisonItem: FC<SituationComparisonItemProps> = ({
  title,
  monthlyTitle,
  value,
  monthlyValue,
  primary = false,
}) => (
  <Flex direction="column" gap="s" className={itemContentStyle}>
    <Flex direction="column" gap="xs">
      <Text ellipsis>{title}</Text>
      <Text variant="displayL" color={primary ? "primary" : undefined} bold>
        <ValueParser value={value} />
      </Text>
    </Flex>
    <Ruler />
    <Flex gap="xs" justify="space-between" align="center">
      <Text ellipsis>{monthlyTitle}</Text>
      <Text variant="bodyL" bold>
        <ValueParser value={monthlyValue} />
      </Text>
    </Flex>
  </Flex>
);
